import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, ChevronDown, ChevronUp, Loader2, MapPin, ScanLine, Shapes, X } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { carteSubnav } from "@/lib/subnavs";
import { MapView } from "@/components/map/MapView";
import { SearchBox } from "@/components/map/SearchBox";
import { ZoneDialog, TourneeSelect } from "@/components/map/ZoneDialog";
import { PointResult, CpResult, TourneeResult, TourneeLegend, DisplaySettings, SidePanel, TourneeChip } from "@/components/map/Panels";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api, errMsg } from "@/lib/api";
import { peut } from "@/lib/permissions";
import { useDouchette } from "@/lib/douchette";
import { CapteurLecteur } from "@/components/colis/ChampLecteur";
import { BarcodeScanner } from "@/components/colis/BarcodeScanner";
import { OPTIONS_LECTURE } from "@/lib/barcode";
import { lireCode } from "@/lib/colisCode";

// Bouton « Scanner » (caméra) : téléphones / tablettes Android et iOS (iPhone,
// iPad — un iPad récent se présente comme un Mac avec écran tactile).
const UA = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
const MOBILE_SCAN = /Android|iPhone|iPad|iPod/i.test(UA)
  || (/Macintosh/.test(UA) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1);
// Caméra : étiquette 2D FedEx (adresse), airwaybills, QR / Data Matrix avec une adresse.
const OPTIONS_CARTE = { ...OPTIONS_LECTURE, formats: [...OPTIONS_LECTURE.formats, "QRCode", "DataMatrix", "Code39"] };
// Code 2D FedEx souvent froissé / de biais : plusieurs tailles et seuillages.
const PASSES_CARTE = [
  { cadre: true }, { echelle: 1 }, { cadre: true, binarizer: "GlobalHistogram" }, { echelle: 0.72, binarizer: "GlobalHistogram" },
  { cadre: true, echelle: 0.7 }, { echelle: 0.5 }, { cadre: true, binarizer: "FixedThreshold" }, { echelle: 0.72, binarizer: "FixedThreshold" },
];
// Secours : n° de suivi (FedEx 34 chiffres, TNT…) vu seul pendant 3 s -> adresse
// cherchée sur la fiche colis ou dans le fichier du transporteur.
const SECOURS_CARTE = { accepter: (t) => !!lireCode(t).type, apresMs: 3000 };
// Codes qui contiennent la destination : étiquette 2D FedEx (« [)> » : adresse
// + CP + commune), code de routage TNT (CP + agence, ex. 11560PZ00000), ou
// texte avec un code postal et des lettres. Les n° de suivi (que des chiffres)
// sont ignorés : sinon la caméra lit d'abord le code 1D FedEx, plus facile,
// au lieu du code 2D avec l'adresse.
const codeAdresse = (t) => {
  const s = (t || "").trim();
  if (s.includes("[)>")) return true;
  if (/^\d{5}\s*[A-Z]{1,3}[\s\d]*$/i.test(s)) return true;   // routage TNT, ex. « 11100J  00000 »
  return /(^|\D)\d{5}(\D|$)/.test(s) && /[A-Za-z]{3,}/.test(s) && !lireCode(s).type;
};

function usePersistentState(key, initial) {
  const [v, setV] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw != null ? JSON.parse(raw) : initial;
    } catch { return initial; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(v)); } catch {} }, [key, v]);
  return [v, setV];
}

/** Écran de téléphone (< 768 px). */
function useMobile() {
  const q = "(max-width: 767px)";
  const [m, setM] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(q).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(q);
    if (!mq) return undefined;
    const f = () => setM(mq.matches);
    mq.addEventListener?.("change", f);
    return () => mq.removeEventListener?.("change", f);
  }, []);
  return m;
}

/** Résultat réduit (téléphone) : une ligne avec le n° de tournée en grand. */
function ResultatCompact({ result, tourneesByCode, onDetails, onClose }) {
  const d = result.data;
  const chip = (t) => t && <TourneeChip key={t.code} t={{ ...t, ...(tourneesByCode[t.code] || {}) }} size="lg" />;
  let titre, sous, chips = [];
  if (result.kind === "point") {
    titre = d.label || d.commune?.nom || "Point sélectionné";
    sous = d.commune ? `${d.commune.nom}${d.cp ? ` · ${d.cp}` : ""}` : d.cp || "";
    if (d.tournee) chips = [chip(d.tournee)];
    else if (d.candidates?.length) chips = d.candidates.map(chip);
  } else if (result.kind === "cp") {
    titre = `Code postal ${d.cp}`;
    sous = d.communes.features.map((f) => f.properties.nom).slice(0, 3).join(", ");
    chips = d.tournees.map(chip);
  } else {
    titre = `Tournée ${d.tournee.code}`;
    sous = d.tournee.libelle || "";
    chips = [chip(d.tournee)];
  }
  return (
    <div className="rounded-xl bg-card/95 backdrop-blur border border-border shadow-xl p-2.5 flex items-center gap-2.5" data-testid="result-compact">
      <div className="flex flex-wrap gap-1 shrink-0 max-w-[45%]" data-testid="result-compact-tournee">
        {chips.length ? chips : <span className="text-xs text-muted-foreground px-1">Aucune tournée</span>}
      </div>
      <button onClick={onDetails} className="min-w-0 flex-1 text-left" data-testid="result-details">
        <span className="flex items-center gap-1 text-sm font-semibold truncate">
          {d.scan ? <ScanLine size={13} className="text-primary shrink-0" /> : result.kind === "point" && <MapPin size={13} className="text-[#ea4335] shrink-0" />}
          <span className="truncate">{titre}</span>
        </span>
        <span className="block text-xs text-muted-foreground truncate">{sous}</span>
      </button>
      <button onClick={onDetails} className="p-1.5 rounded-md hover:bg-accent text-muted-foreground" aria-label="Détails"><ChevronUp size={18} /></button>
      <button onClick={onClose} className="p-1.5 rounded-md hover:bg-accent text-muted-foreground" aria-label="Fermer"><X size={18} /></button>
    </div>
  );
}

export default function CartePage() {
  const { user } = useAuth();
  const mobile = useMobile();
  const [details, setDetails] = useState(false);   // téléphone : résultat déplié
  const { theme } = useTheme();
  const peutDessiner = peut(user, "carte_dessin");      // dessiner / modifier les zones
  const peutAffecter = peut(user, "carte_affecter");    // rattacher communes / zones aux tournées
  const mapRef = useRef(null);
  const [params, setParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [tournees, setTournees] = useState([]);
  const [communes, setCommunes] = useState(null);
  const [zones, setZones] = useState([]);
  const [deps, setDeps] = useState(null);

  const [baseLayer, setBaseLayer] = usePersistentState("carte.fond", theme === "dark" ? "sombre" : "plan");
  const [colorBy, setColorBy] = usePersistentState("carte.couleur", "tournee");
  const [show, setShow] = usePersistentState("carte.couches", { communes: true, zones: true, labels: true, deps: true });
  const [hidden, setHidden] = useState(() => new Set());
  const [tab, setTab] = useState("tournees");

  const [highlight, setHighlight] = useState(null);
  const [marker, setMarker] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const [drawMode, setDrawMode] = useState(null); // null | "draw" | "edit"
  const [zoneDialog, setZoneDialog] = useState(null);
  const [communeDialog, setCommuneDialog] = useState(null);

  const tourneesByCode = useMemo(() => Object.fromEntries(tournees.map((t) => [t.code, t])), [tournees]);
  const ctx = useMemo(() => ({
    tourneesByCode,
    colorBy,
    secteurs: [...new Set(tournees.map((t) => t.secteur).filter(Boolean))],
    ssts: [...new Set(tournees.map((t) => t.sst).filter(Boolean))],
  }), [tourneesByCode, tournees, colorBy]);

  const loadAll = useCallback(async () => {
    const [t, c, z, d] = await Promise.all([
      api.get("/tournees"), api.get("/carte/communes"), api.get("/zones"), api.get("/carte/departements"),
    ]);
    setTournees(t.data.tournees);
    setCommunes(c.data);
    setZones(z.data);
    setDeps(d.data);
    return c.data;
  }, []);

  const fitServed = useCallback((fc) => {
    const src = fc || communes;
    const served = src?.features?.filter((f) => f.properties.statut !== "aucune");
    if (served?.length) mapRef.current?.fitGeo({ type: "FeatureCollection", features: served }, 11);
  }, [communes]);

  // ---------- Recherches ----------
  const clearResult = useCallback(() => {
    setResult(null); setHighlight(null); setMarker(null); setDetails(false);
  }, []);

  const localiser = useCallback(async ({ lat, lon, cp, label, reverse }) => {
    setBusy(true);
    setMarker({ lat, lon });
    try {
      const { data } = await api.get("/localiser", { params: { lat, lon, cp, label, reverse } });
      setResult({ kind: "point", data });
      setHighlight(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, []);

  // Scan d'une étiquette (Zebra / douchette) : commune + CP -> position ;
  // Narbonne et Carcassonne : adresse exacte (rue + n°).
  const scanEtiquette = useCallback(async (code, secours = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/carte/scan", { code });
      if (data.precision === "cp") {
        toast.success(`Code postal ${data.lu.cp} (plusieurs communes)`);
        await searchCpRef.current?.(data.lu.cp);
        return;
      }
      const L = data.localisation;
      mapRef.current?.flyTo(L.lat, L.lon, data.precision === "commune" ? 13 : 17);
      setMarker({ lat: L.lat, lon: L.lon });
      setHighlight(null);
      setResult({ kind: "point", data: { ...L, scan: { ...data.lu, precision: data.precision, alerte: data.alerte } } });
      toast.success(`${data.lu.cp} ${data.lu.commune}${L.tournee ? ` → tournée ${L.tournee.code}` : ""}`);
    } catch (e) {
      if (secours) {
        // n° de suivi lu faute de mieux, mais adresse inconnue : on rouvre la caméra
        toast.error("Code 2D pas encore lu, et ce n° de colis n'a pas d'adresse connue. Visez le grand code 2D (bien à plat, éclairé).", { duration: 6000 });
        setCameraScan(true);
      } else toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, []);

  const [cameraScan, setCameraScan] = useState(false);
  const searchCpRef = useRef(null);
  const scanActif = !zoneDialog && !drawMode && !cameraScan && peut(user, "carte_scan");
  useDouchette(scanEtiquette, scanActif);

  const searchCp = useCallback(async (cp) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/recherche/cp/${cp}`);
      // Ajoute à la carte les communes de ce CP qui n'y figuraient pas encore.
      setCommunes((fc) => {
        if (!fc) return fc;
        const have = new Set(fc.features.map((f) => f.properties.code));
        const extra = data.communes.features.filter((f) => !have.has(f.properties.code));
        return extra.length ? { ...fc, features: [...fc.features, ...extra] } : fc;
      });
      setHighlight({ communes: new Set(data.communes.features.map((f) => f.properties.code)), zones: new Set(data.zones_ids) });
      setMarker(null);
      setResult({ kind: "cp", data });
      if (data.communes.features.length) mapRef.current?.fitGeo(data.communes, 13);
      else toast.info(`Aucune commune d'Occitanie avec le code postal ${cp}.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, []);
  searchCpRef.current = searchCp;

  const searchTournee = useCallback(async (code) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/recherche/tournee/${code}`);
      const communeSet = new Set(data.communes);
      const zoneSet = new Set(data.zones_ids);
      setHighlight({ communes: communeSet, zones: zoneSet });
      setMarker(null);
      setResult({ kind: "tournee", data });
      setHidden((h) => { if (!h.has(code)) return h; const n = new Set(h); n.delete(code); return n; });
      const feats = [
        ...(communes?.features || []).filter((f) => communeSet.has(f.properties.code)),
        ...zones.filter((z) => zoneSet.has(z.id)).map((z) => ({ type: "Feature", geometry: z.geometry, properties: {} })),
      ];
      if (feats.length) mapRef.current?.fitGeo({ type: "FeatureCollection", features: feats }, 14);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, [communes, zones]);

  // ---------- Chargement initial + paramètres d'URL ----------
  const initDone = useRef(false);
  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;
    loadAll()
      .then((fc) => {
        const cp = params.get("cp");
        const t = params.get("tournee");
        const zone = params.get("zone");
        if (!cp && !t && !zone) setTimeout(() => fitServed(fc), 50);
      })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading) return;
    const cp = params.get("cp");
    const t = params.get("tournee");
    const zoneId = params.get("zone");
    if (cp) searchCp(cp);
    else if (t) searchTournee(t);
    else if (zoneId) {
      const z = zones.find((x) => x.id === zoneId);
      if (z) {
        setHighlight({ zones: new Set([z.id]) });
        mapRef.current?.fitGeo(z.geometry, 16);
      }
    }
    if (cp || t || zoneId) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // ---------- Administration : zones ----------
  const startDraw = () => {
    setZoneDialog(null);
    mapRef.current?.startDraw();
  };

  useEffect(() => {
    if (!drawMode) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (drawMode === "draw") mapRef.current?.cancelDraw();
      else if (drawMode === "edit") cancelEditShape();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawMode]);

  const refreshPoint = useCallback(() => {
    if (result?.kind === "point") {
      const d = result.data;
      localiser({ lat: d.lat, lon: d.lon, cp: d.cp, label: d.label });
    }
  }, [result, localiser]);

  const saveZone = async (fields) => {
    const z = zoneDialog.zone;
    try {
      if (z.id) {
        const { data } = await api.put(`/zones/${z.id}`, fields);
        setZones((list) => list.map((x) => (x.id === data.id ? data : x)));
        toast.success(`Zone « ${data.nom} » mise à jour`);
      } else {
        const { data } = await api.post("/zones", { ...fields, geometry: z.geometry });
        setZones((list) => [...list, data]);
        toast.success(`Zone « ${data.nom} » créée${data.tournee ? ` — tournée ${data.tournee}` : ""}`);
      }
      setZoneDialog(null);
      refreshPoint();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deleteZone = async (z) => {
    try {
      await api.delete(`/zones/${z.id}`);
      setZones((list) => list.filter((x) => x.id !== z.id));
      setZoneDialog(null);
      toast.success(`Zone « ${z.nom} » supprimée`);
      refreshPoint();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const editingZone = useRef(null);
  const startEditShape = (z) => {
    setZoneDialog(null);
    setHighlight(null);
    editingZone.current = z;
    // laisse le temps à la couche des zones de se reconstruire sans surbrillance
    setTimeout(() => {
      if (!mapRef.current?.startEditZone(z.id)) toast.error("Zone introuvable sur la carte (masquée ?)");
    }, 60);
  };

  const finishEditShape = async () => {
    const r = mapRef.current?.finishEditZone();
    const z = editingZone.current;
    editingZone.current = null;
    if (!r || !z) return;
    try {
      const { data } = await api.put(`/zones/${z.id}`, {
        nom: z.nom, couleur: z.couleur, tournee: z.tournee, sst: z.sst, notes: z.notes, geometry: r.geometry,
      });
      setZones((list) => list.map((x) => (x.id === data.id ? data : x)));
      toast.success("Tracé enregistré");
      refreshPoint();
    } catch (e) {
      toast.error(errMsg(e));
      setZones((list) => [...list]);
    }
  };

  const cancelEditShape = () => {
    mapRef.current?.cancelEditZone();
    editingZone.current = null;
    setZones((list) => [...list]); // reconstruit la couche avec le tracé d'origine
  };

  // ---------- Administration : commune ----------
  const openCommuneDialog = (code) => {
    const f = communes?.features.find((x) => x.properties.code === code);
    const p = f?.properties || {};
    const mode = p.non_affectee ? "aucune" : p.source === "manuel" && p.tournee ? "tournee" : "defaut";
    setCommuneDialog({ code, nom: p.nom || code, props: p, mode, tournee: mode === "tournee" ? p.tournee : null });
  };

  const saveCommune = async () => {
    try {
      const { mode, tournee } = communeDialog;
      const { data } = await api.put(`/carte/communes/${communeDialog.code}/tournee`, { mode, tournee: mode === "tournee" ? tournee : null });
      setCommunes((fc) => {
        const exists = fc.features.some((f) => f.properties.code === data.properties.code);
        return {
          ...fc,
          features: exists ? fc.features.map((f) => (f.properties.code === data.properties.code ? data : f)) : [...fc.features, data],
        };
      });
      const p = data.properties;
      toast.success(
        mode === "aucune" ? `${communeDialog.nom} : non affectée`
        : mode === "tournee" ? `${communeDialog.nom} → tournée ${tournee}`
        : `${communeDialog.nom} : affectation par défaut${p.tournee ? ` (tournée ${p.tournee})` : p.statut === "multi" ? " (code postal partagé)" : " (aucune tournée)"}`,
      );
      setCommuneDialog(null);
      refreshPoint();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const zoneById = (id) => zones.find((z) => z.id === id);

  // Affectation rapide d'un quartier / d'une zone depuis la fiche résultat.
  const assignZone = async (zone, code) => {
    try {
      const { data } = await api.patch(`/zones/${zone.id}/tournee`, { tournee: code });
      setZones((list) => list.map((x) => (x.id === data.id ? data : x)));
      toast.success(code ? `« ${data.nom} » → tournée ${code}` : `« ${data.nom} » : non affecté`);
      refreshPoint();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const mapTournees = tournees.filter((t) => !t.hors_carte);

  // Téléphone : chaque nouveau résultat s'affiche d'abord réduit.
  useEffect(() => { setDetails(false); }, [result]);

  return (
    <AppShell fullHeight title="Visualisation des tournées de livraison" subnav={carteSubnav()}>
      <CapteurLecteur onScan={scanEtiquette} actif={scanActif} />
      {/* Téléphone : résultat en bas, compact (n° de tournée bien visible), la carte reste dégagée. */}
      {result && mobile && (
        <div className="absolute z-20 left-2 right-2 bottom-2 pointer-events-auto" data-testid="result-mobile">
          {details ? (
            <div className="relative">
              <button onClick={() => setDetails(false)} className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 h-6 px-3 rounded-full bg-card border border-border shadow text-xs flex items-center gap-1" data-testid="result-reduire">
                <ChevronDown size={13} /> Réduire
              </button>
              <div className="[&>div]:max-h-[45vh]">
                  {result.kind === "point" && (
                    <PointResult
                      data={result.data}
                      tourneesByCode={tourneesByCode}
                      tournees={mapTournees}
                      onAssignZone={assignZone}
                      droits={{ dessin: peutDessiner, affecter: peutAffecter }}
                      onClose={clearResult}
                      onTournee={searchTournee}
                      onEditZone={(id) => { const z = zoneById(id); if (z) setZoneDialog({ zone: z }); }}
                      onAffecterCommune={openCommuneDialog}
                      onDrawHere={startDraw}
                    />
                  )}
                  {result.kind === "cp" && <CpResult data={result.data} tourneesByCode={tourneesByCode} onClose={clearResult} onTournee={searchTournee} />}
                  {result.kind === "tournee" && <TourneeResult data={result.data} onClose={clearResult} onCp={searchCp} tourneesByCode={tourneesByCode} />}
              </div>
            </div>
          ) : (
            <ResultatCompact result={result} tourneesByCode={tourneesByCode} onDetails={() => setDetails(true)} onClose={clearResult} />
          )}
        </div>
      )}
      <div className={`${drawMode === "draw" ? "map-drawing" : ""} ${result && mobile ? "carte-resultat-mobile" : ""}`}>
        <MapView
          ref={mapRef}
          baseLayer={baseLayer}
          departements={deps}
          communes={communes}
          zones={zones}
          ctx={ctx}
          hidden={hidden}
          show={show}
          highlight={highlight}
          marker={marker}
          onMapClick={(ll) => localiser({ lat: ll.lat, lon: ll.lng, reverse: true })}
          onDrawn={(geometry) => setZoneDialog({ zone: { geometry } })}
          onDrawStateChange={setDrawMode}
        />
      </div>

      {/* Panneau gauche */}
      <div className="absolute z-10 top-3 left-3 right-3 md:right-auto md:w-[380px] md:bottom-3 flex flex-col gap-3 pointer-events-none">
        <div className="pointer-events-auto">
          <SearchBox
            onAddress={(it) => { mapRef.current?.flyTo(it.lat, it.lon, 16); localiser({ lat: it.lat, lon: it.lon, cp: it.postcode, label: it.label }); }}
            onCp={searchCp}
            onTournee={searchTournee}
            onClear={clearResult}
            tourneeCodes={tournees.map((t) => t.code)}
          />
        </div>
        {busy && !result && (
          <div className="pointer-events-auto rounded-xl bg-card border border-border p-3 text-sm flex items-center gap-2 shadow-lg">
            <Loader2 size={15} className="animate-spin" /> Recherche…
          </div>
        )}
        {result && !mobile && (
          <div className="pointer-events-auto shrink-0">
            {result.kind === "point" && (
              <PointResult
                data={result.data}
                tourneesByCode={tourneesByCode}
                tournees={mapTournees}
                onAssignZone={assignZone}
                droits={{ dessin: peutDessiner, affecter: peutAffecter }}
                onClose={clearResult}
                onTournee={searchTournee}
                onEditZone={(id) => { const z = zoneById(id); if (z) setZoneDialog({ zone: z }); }}
                onAffecterCommune={openCommuneDialog}
                onDrawHere={startDraw}
              />
            )}
            {result.kind === "cp" && <CpResult data={result.data} tourneesByCode={tourneesByCode} onClose={clearResult} onTournee={searchTournee} />}
            {result.kind === "tournee" && <TourneeResult data={result.data} onClose={clearResult} onCp={searchCp} tourneesByCode={tourneesByCode} />}
          </div>
        )}
        <div className="pointer-events-auto min-h-0 flex flex-col md:flex-1">
          <SidePanel tab={tab} setTab={setTab}>
            {loading ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement de la carte…</p>
            ) : tab === "tournees" ? (
              <TourneeLegend
                tournees={tournees}
                ctx={ctx}
                hidden={hidden}
                setHidden={setHidden}
                onTournee={searchTournee}
                selected={result?.kind === "tournee" ? result.data.tournee.code : null}
              />
            ) : (
              <DisplaySettings
                baseLayer={baseLayer} setBaseLayer={setBaseLayer}
                colorBy={colorBy} setColorBy={setColorBy}
                show={show} setShow={setShow}
                onOccitanie={() => mapRef.current?.fitOccitanie()}
                onFitTournees={() => fitServed()}
              />
            )}
          </SidePanel>
        </div>
      </div>

      {/* Barre d'outils (permission « Dessiner des zones ») */}
      {peutDessiner && (
        <div className="absolute z-10 top-3 right-3 hidden md:flex flex-col items-end gap-2">
          {!drawMode && (
            <Button onClick={startDraw} className="shadow-lg" data-testid="draw-zone-button">
              <Shapes size={16} /> Dessiner une zone
            </Button>
          )}
        </div>
      )}
      {peutDessiner && !drawMode && (
        <button
          onClick={startDraw}
          className={`md:hidden absolute z-10 ${result ? "bottom-[10.5rem]" : "bottom-24"} right-3 w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-xl flex items-center justify-center`}
          aria-label="Dessiner une zone"
        >
          <Shapes size={20} />
        </button>
      )}

      {MOBILE_SCAN && peut(user, "carte_scan") && !drawMode && (
        <button
          onClick={() => setCameraScan(true)}
          className={`absolute z-10 right-3 ${peutDessiner ? (result && mobile ? "bottom-[14.5rem]" : "bottom-40") : (result && mobile ? "bottom-[10.5rem]" : "bottom-24")} md:bottom-32 w-12 h-12 rounded-full bg-sky-600 text-white shadow-xl flex items-center justify-center`}
          aria-label="Scanner une étiquette avec la caméra"
          title="Scanner une étiquette"
          data-testid="btn-scan-camera"
        >
          <ScanLine size={22} />
        </button>
      )}
      <Dialog open={cameraScan} onOpenChange={(o) => !o && setCameraScan(false)}>
        <DialogContent className="sm:max-w-lg p-4" data-testid="scan-camera-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ScanLine size={18} /> Scanner une étiquette</DialogTitle>
            <DialogDescription>FedEx : visez le grand code 2D (adresse complète). TNT : le code-barres de routage (ex. 11560PZ00000, sous le « 1A »). La commune, ou l'adresse exacte à Narbonne et Carcassonne, s'affiche sur la carte.</DialogDescription>
          </DialogHeader>
          {cameraScan && (
            <BarcodeScanner
              options={OPTIONS_CARTE}
              accepter={codeAdresse}
              passes={PASSES_CARTE}
              haute
              secours={SECOURS_CARTE}
              aideIgnore="visez le code 2D (FedEx) ou le code de routage (TNT)"
              aide="FedEx : code 2D (grand carré) · TNT : code-barres sous le « 1A »"
              onDetected={(r) => { setCameraScan(false); scanEtiquette(r.text, r.secours); }}
              onCancel={() => setCameraScan(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {drawMode && (
        <div className="absolute z-20 bottom-6 left-1/2 -translate-x-1/2 w-[min(560px,calc(100%-24px))] rounded-xl bg-card border border-primary/50 shadow-2xl p-3 flex flex-col sm:flex-row items-center gap-3" data-testid="draw-banner">
          <p className="text-sm flex-1">
            {drawMode === "draw"
              ? <>Cliquez sur la carte pour placer les points du contour, puis <b>cliquez sur le premier point</b> pour fermer la zone.</>
              : <>Déplacez les points (ou cliquez au milieu d'un côté pour en ajouter), clic droit sur un point pour le supprimer.</>}
          </p>
          <div className="flex gap-2 shrink-0">
            {drawMode === "edit" && (
              <Button size="sm" onClick={finishEditShape} data-testid="edit-shape-save"><Check size={14} /> Enregistrer le tracé</Button>
            )}
            <Button size="sm" variant="outline" onClick={() => (drawMode === "draw" ? mapRef.current?.cancelDraw() : cancelEditShape())}>
              <X size={14} /> Annuler
            </Button>
          </div>
        </div>
      )}

      <ZoneDialog
        open={!!zoneDialog}
        zone={zoneDialog?.zone}
        tournees={mapTournees}
        onClose={() => setZoneDialog(null)}
        onSave={saveZone}
        onDelete={deleteZone}
        onEditShape={startEditShape}
      />

      <Dialog open={!!communeDialog} onOpenChange={(o) => !o && setCommuneDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Affectation de {communeDialog?.nom}</DialogTitle>
            <DialogDescription>
              S'applique à toute la commune. Les zones dessinées à l'intérieur restent prioritaires.
              {communeDialog?.props?.statut === "multi" && " Son code postal est actuellement partagé entre plusieurs tournées."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="commune-modes">
            {[
              ["tournee", "Affecter à une tournée", "Toute la commune est rattachée à la tournée choisie."],
              ["aucune", "Non affectée", "La commune n'a plus de tournée (ni par la liste des villages, ni par le code postal)."],
              ["defaut", "Par défaut", "Revient à la liste des villages importée, sinon au code postal."],
            ].map(([value, label, hint]) => (
              <button
                key={value}
                type="button"
                onClick={() => setCommuneDialog((d) => ({ ...d, mode: value }))}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${communeDialog?.mode === value ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}
                data-testid={`commune-mode-${value}`}
              >
                <span className={`block text-sm font-semibold ${communeDialog?.mode === value ? "text-primary" : ""}`}>{label}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{hint}</span>
              </button>
            ))}
          </div>
          {communeDialog?.mode === "tournee" && (
            <TourneeSelect
              tournees={mapTournees}
              value={communeDialog?.tournee}
              onChange={(v) => setCommuneDialog((d) => ({ ...d, tournee: v }))}
              testId="commune-tournee"
            />
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCommuneDialog(null)}>Annuler</Button>
            <Button onClick={saveCommune} disabled={communeDialog?.mode === "tournee" && !communeDialog?.tournee} data-testid="commune-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
